import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getAuthToken, handleAuthTokenError, BASEURI } from '../utils';
import PieBox, { PiesContainer } from './PieBox';
import StationGrid, { GridContainer } from './AdminStationGrid';
import GraphGrid from './AdminGraphGrid';
import StationsPawStatusChart from './StationsPawStatusChart';
import UserGrid from './AdminUserGrid';
import GMap from './Maps';
import ConfirmDeleteDialog from '../general/ConfirmDeleteDialog';
import WeatherReport from './reports/WeatherReport';
import { RefreshPortal } from '../general/Layout';
import RefreshButton from '../general/RefreshButton';
import ForecastCharts from './reports/forecast_report/ForecastCharts';
import WellsReportChartContainer from './reports/wells_report/WellsReport';
import { useIntl } from 'react-intl';
import { getIcon } from './markers';
import { AgvMap } from '@agviewer/agviewermap-react';
import '@agviewer/agviewermap-react/style.css';
import { getSecureConfig } from '../services/configService';
import { Dashboard } from '../general/Dashboard';
import Spinner from '../general/Spinner';


function Admin(props) {

    const [stations, setStations] = useState({
        all: [],
        hasLowBattery: [],
        isLagging: [],
        isNormal: []
    });
    const [percentOfOrgsSensorsWithoutData, setPercentOfOrgsSensorsWithoutData] = useState(0)

    const [graphs, setGraphs] = useState({
        all: [],
        rRange: [],
        yRange: [],
        gRange: [],
        bRange: []
    });

    const [users, setUsers] = useState({
        all: [],
        today: [],
        thisMonth: [],
        inactive: []
    });
    const [insideMap, setInsideMap] = useState(window.screen.width > 700);
    const [stationGridData, setStationGridData] = useState([]);
    const [graphGridData, setGraphGridData] = useState([]);
    const [userGridData, setUserGridData] = useState([]);
    const [maps, setMaps] = useState([]);

    const [isAdmin, setIsAdmin] = useState(false);
    const [isOrganization, setIsOrganization] = useState(false);
    const [toDelete, setToDelete] = useState(undefined);
    const [deleting, setDeleting] = useState(false);
    const [isWorking, setIsworking] = useState({
        loadStations: false,
        loadGraphs: false,
        loadUsers: false
    });
    // prevent reload animation on first access
    const [notIntial, setNotInitial] = useState(false);
    const [loading, setLoading] = useState(false);
    const [forecastCharts, setForecastCharts] = useState([]);
    const [wellsCharts, SetWellsCharts] = useState([]);
    const [filter, setFilter] = useState('All');
    const intl = useIntl();
    const [markers, setMarkers] = useState([]);
    const [refreshAll, setRefreshAll] = useState(false);
    // Default to the new map; only an explicit opt-out ('false') keeps the classic map
    const [showNewMap, setShowNewMap] = useState(localStorage.getItem('showNewMap') !== 'false');
    // Map toast mounts expanded with the announcement, then collapses into the switch/return chip (auto after 8s or via close).
    // On mobile (< sm, 640px) it starts collapsed — no room for the full sentence next to the map controls.
    const [mapToastExpanded, setMapToastExpanded] = useState(() => window.innerWidth >= 640);

    useEffect(() => {
        const collapseTimer = setTimeout(() => setMapToastExpanded(false), 8000);
        return () => clearTimeout(collapseTimer);
    }, []);
    const [mapConfig, setMapConfig] = useState({});
    const [showSidePanel, setShowSidePanel] = useState(false);
    const [selectedDashboardId, setSelectedDashboardId] = useState(null);
    const [selectedChartId, setSelectedChartId] = useState(null);
    const [showCurtain, setShowCurtain] = useState(false);
    const [mapAuthToken, setMapAuthToken] = useState(null);

    useEffect(() => {
        localStorage.setItem('showNewMap', showNewMap);
    }, [showNewMap])

    // Fetch fresh token for AgvMap when map is shown (getAuthToken handles refresh)
    useEffect(() => {
        if (!showNewMap) {
            setMapAuthToken(null);
            return;
        }
        getAuthToken()
            .then(t => t)
            .catch(handleAuthTokenError)
            .then(token => {
                if (token === false) {
                    props.logout();
                    return;
                }
                setMapAuthToken(token);
            });
    }, [showNewMap, props.logout])

    // Initialize markers from props.markers
    useEffect(() => {
        // setMarkers(props.markers)

        if (props.markers.length < 1)
            return

        // Determine markers colors & types
        setMarkers(props.markers.map(item => {
            const [icon, type] = getIcon(item)

            return {
                icon: icon,
                type: type,
                ...item
            }
        }))

    }, [props.markers])


    useEffect(() => {
        if ((isWorking.loadGraphs || isWorking.loadStations || isWorking.loadUsers) && notIntial) {
            setLoading(true);
        }
        else { setLoading(false); }
    }, [isWorking]);


    //refs for scrolling to grids
    const stationsRef = useRef(null);
    const graphsRef = useRef(null);
    const usersRef = useRef(null);
    const adminPageTopRef = useRef(null);
    const tabsRef = useRef(null);

    const loadStations = () => {
        setIsworking({ ...isWorking, loadStations: true });
        let url = BASEURI + '/api/dashboard/station/',
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    this.setState({ loggedin: false });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        return response.json();
                    })
                    .then((stations) => {
                        !notIntial && setNotInitial(true);
                        if (stations) {
                            setIsworking({ ...isWorking, loadStations: false })
                            setStations({
                                all: stations,
                                hasLowBattery: stations.filter(station => station.is_battery_low && !station.is_lagging),
                                isLagging: stations.filter(station => station.is_lagging),
                                isNormal: stations.filter(station => !station.is_battery_low && !station.is_lagging)
                            });
                        }
                    })
                    .catch(error => {
                        console.error('Error:', error.code);
                    });
            });
    }

    const loadGraphs = () => {
        setIsworking({ ...isWorking, loadGraphs: true });
        let url = BASEURI + '/api/dashboard/graph/paw/',
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };
        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    this.setState({ loggedin: false });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        return response.json();
                    })
                    .then((graphs) => {
                        setIsworking({ ...isWorking, loadGraphs: false });
                        if (Array.isArray(graphs)) {

                            let rRange = graphs.filter(graph => {
                                try {
                                    let v = graph.last_value[1];
                                    return v <= 30;
                                } catch {
                                    return true;
                                }

                            })

                            let yRange = graphs.filter(graph => {
                                try {
                                    let v = graph.last_value[1];
                                    return (30 < v) && (v <= 70);
                                } catch {
                                    return false;
                                }

                            });

                            let gRange = graphs.filter(graph => {
                                try {
                                    let v = graph.last_value[1];
                                    return (70 < v) && (v <= 100);
                                } catch {
                                    return false;
                                }

                            });

                            let bRange = graphs.filter(graph => {
                                try {
                                    let v = graph.last_value[1];
                                    return v > 100;
                                } catch {
                                    return false;
                                }

                            });

                            setGraphs({
                                all: graphs,
                                rRange: rRange,
                                yRange: yRange,
                                gRange: gRange,
                                bRange: bRange
                            });

                        }
                    })
                    .catch(error => {
                        console.error('Error:', error);
                    });
            });

    }

    const loadUsers = () => {
        setIsworking({ ...isWorking, loadUsers: true });

        let url = BASEURI + '/api/dashboard/user/',
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };
        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    this.setState({ loggedin: false });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        return response.json();
                    })
                    .then((allUsers) => {
                        setIsworking({ ...isWorking, loadUsers: false });

                        //multiplier to convert miliseconds to hours
                        // divide by 1000 * 60 * 60 * 24
                        let divider = 1000 * 60 * 60 * 24;

                        let today = allUsers.filter(user => {
                            try {
                                let diffMS = Math.abs(new Date() - new Date(user.user.last_login));
                                return Math.round(diffMS / divider) <= 1;
                            } catch (error) {
                                return false;
                            }

                        }),
                            thisMonth = allUsers.filter(user => {
                                try {
                                    let diffMS = Math.abs(new Date() - new Date(user.user.last_login));
                                    return Math.round(diffMS / divider) <= 30;
                                }
                                catch (error) {
                                    return false;
                                }
                            }),
                            inactive = allUsers.filter(user => {
                                try {
                                    let diffMS = Math.abs(new Date() - new Date(user.user.last_login));
                                    return Math.round(diffMS / divider) > 30;
                                } catch (error) {
                                    return true;
                                }

                            });

                        setUsers({
                            all: allUsers,
                            today: today,
                            thisMonth: thisMonth,
                            inactive: inactive,
                        });
                    })
                    .catch(error => {
                        console.error('Error:', error.code);
                    });
            });
    }

    const loadForecastCharts = () => {

        let url = BASEURI + '/api/meteoblue/charts/forecast_charts/',
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    setLoading(false);
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        return response.json();
                    })
                    .then((charts) => {
                        setForecastCharts(Array.isArray(charts) ? charts : []);
                    })
                    .catch(error => {
                        console.error('Error:', error.code);
                    });
            });

    }
    const loadWellsCharts = () => {
        let url = BASEURI + '/api/smart-wells/charts/',
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    setLoading(false);
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        return response.json();
                    })
                    .then((charts_list) => {
                        SetWellsCharts(Array.isArray(charts_list) ? charts_list : []);
                    })
                    .catch(error => {
                        console.error('Error:', error.code);
                    });
            });
    }

    const getOrgStatistics = () => {
        let url = BASEURI + '/api/dashboard/org-statistics/',
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    setLoading(false);
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        return response.json();
                    })

                    .then((statistics) => {
                        console.log('the statistics: ', statistics)
                        setPercentOfOrgsSensorsWithoutData(
                            statistics?.percent_of_sensors_without_data
                        )
                    })
                    .catch(error => {
                        console.error('Error:', error.code);
                    });
            });
    }
    const refresh = () => {
        setRefreshAll(!refreshAll);
        loadStations();
        loadGraphs();
        loadUsers();
        loadForecastCharts();
        loadWellsCharts();
        getOrgStatistics();
    }

    useEffect(() => loadStations(), []);

    useEffect(() => getOrgStatistics(), []);

    useEffect(() => loadGraphs(), []);

    useEffect(() => loadUsers(), []);

    useEffect(() => {
        loadForecastCharts();
    }, []);

    useEffect(() => loadWellsCharts(), []);

    useEffect(() => {
        // document.getElementById('refresh').addEventListener('click', refresh);
        // upgrade mdl tabs
        if (window.componentHandler)
            window.componentHandler.upgradeElement(tabsRef.current);
        // return () => document.getElementById('refresh').removeEventListener('click', refresh);
    }, []);

    useEffect(() => {
        // check if user is superuser/admin
        let url = BASEURI + '/api/dashboard/superuser/',
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };
        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    props.logout();
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => response.json())
                    .then((client) => {

                        setIsAdmin(client.user.is_superuser);
                        setIsOrganization(client.is_organization);

                    })
                    .catch(error => {
                        console.error('Error:', error.code);
                    });
            });


    }, []);

    //load maps
    useEffect(() => {
        let url = BASEURI + '/api/dashboard/map/primary/',
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    this.setState({ loggedin: false });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        return response.json();
                    })
                    .then((map) => {
                        if (map)
                            setMaps([map]);
                    })
                    .catch(error => {
                        console.error('Error:', error.code);
                    });
            });
    }, [])

    //effect to update station grid when an item is deleted
    useEffect(() => {
        setStationGridData(stations.all);
    }, [stations])


    function enterStationEdit(dataItem) {
        setStationGridData(stationGridData.map(station =>
            station.serial === dataItem.serial ?
                { ...station, inEdit: true } : station
        )
        )
    }

    function updateStation(dataItem) {

        const data = stationGridData.map(station =>
            station.serial === dataItem.serial ?
                { ...station, inEdit: undefined } : station
        );

        setStationGridData(data);

        let url = BASEURI + `/api/stations/${dataItem.serial}/`,
            options = {
                method: 'PUT',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
                body: JSON.stringify(dataItem)
            };
        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    this.setState({ loggedin: false });
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;


                fetch(url, options)
                    .then((response) => {
                        return response.json();
                    })
                    .then((stationList) => {

                        //update original/base grid data
                        try {
                            setStations({
                                all: stations.all.map(station =>
                                    station.serial === dataItem.serial ?
                                        { ...dataItem, inEdit: undefined } : station
                                ),
                                hasLowBattery: stations.hasLowBattery.map(station =>
                                    station.serial === dataItem.serial ?
                                        { ...dataItem, inEdit: undefined } : station
                                ),
                                isLagging: stations.isLagging.map(station =>
                                    station.serial === dataItem.serial ?
                                        { ...dataItem, inEdit: undefined } : station
                                ),
                                isNormal: stations.isNormal.map(station =>
                                    station.serial === dataItem.serial ?
                                        { ...dataItem, inEdit: undefined } : station
                                )
                            });
                        } catch (error) {
                            console.log(error);
                        }

                    })
                    .catch(error => {
                        console.error('Error:', error.code);

                        //reset current grid data back to original item
                        // const originalStationItem = stations.all.find(station => station.serial === dataItem.serial);
                        // const data = stationGridData.map(station => station.serial === dataItem.serial ? originalStationItem : station);
                        // setStationGridData(data);
                        cancelStationEdit(dataItem);

                    });
            });
    }

    function cancelStationEdit(dataItem) {
        const originalStationItem = stations.all.find(station => station.serial === dataItem.serial);
        const data = stationGridData.map(station => station.serial === dataItem.serial ? originalStationItem : station);
        setStationGridData(data);
    }

    function onStationChange(event) {
        const data = stationGridData.map(station =>
            station.serial === event.dataItem.serial ?
                { ...station, [event.field]: event.value } : station
        );
        setStationGridData(data);
    }

    function enterDelete(dataItem) {
        setToDelete(dataItem);
    }

    function deleteStation() {
        setDeleting(true);
        let url = BASEURI + `/api/stations/${toDelete.serial}/`,
            options = {
                method: 'DELETE',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };
        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    // this.setState({loggedin:false});
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then(() => {
                        setToDelete(undefined);
                        setDeleting(false);
                        loadStations();
                    })
                    .catch(error => {
                        setDeleting(false);
                        console.error('Error:', error.code);
                    });
            });

    }


    function setStationGrid(e, set) {
        if (set === 'Normal')
            setStationGridData(stations.isNormal);
        else if (set === 'Lagging')
            setStationGridData(stations.isLagging);
        else if (set === 'Low Battery')
            setStationGridData(stations.hasLowBattery);
        else
            setStationGridData(stations.all);
        executeRef(stationsRef);
        e.preventDefault();
    }

    function handlePawGraphFilter(graphsList) {
        if (graphsList && Array.isArray(graphsList)) {
            const ids = graphsList.map(g => g.id);
            setGraphGridData(graphs.all.filter(g => ids.includes(g.id)));
            executeRef(graphsRef);
        }
    }

    const [sidePanelTitle, setSidePanelTitle] = useState('');

    function handleShowAnalysis(markerContent) {
        console.log('handleShowAnalysis called with:', markerContent);
        if (markerContent && markerContent.paw_graphs && markerContent.paw_graphs.length > 0) {

            // The logs show that the 'graphs' array contains objects where 'chart' is an OBJECT.
            // We can directly access tab and chart ID.

            const graph = markerContent.paw_graphs[0];
            console.log('Processing graph for analysis:', graph);

            if (graph?.chart && graph.chart.tab && graph.chart.id) {
                const tabId = graph.chart.tab;
                const chartId = graph.chart.id;

                console.log('Found tab:', tabId, 'chart:', chartId);

                setSelectedDashboardId(tabId);
                setSelectedChartId(chartId);

                // Fetch dashboard details to get the name
                let url = `${BASEURI}/api/tab/rud/${tabId}/`;
                let options = {
                    method: 'GET',
                    headers: { "Content-Type": "application/json; charset=utf-8" }
                };

                getAuthToken()
                    .then(token => token)
                    .catch(handleAuthTokenError)
                    .then(token => {
                        if (token) {
                            options.headers.Authorization = `Bearer ${token}`;
                            fetch(url, options)
                                .then(res => res.json())
                                .then(tab => {
                                    if (tab && tab.name) {
                                        setSidePanelTitle(tab.name);
                                    }
                                })
                                .catch(err => console.error('Error fetching tab:', err));
                        }
                    });

                setShowSidePanel(true);
            } else {
                console.error('Graph does not have valid chart or tab information', graph);
            }
        } else {
            console.error('Invalid marker content or no graphs');
        }
    }

    function setGraphGrid(e, set) {

        // console.log(set);
        if (set === 'Stress severo')
            setGraphGridData(graphs.rRange);
        else if (set === 'Inicia stress')
            setGraphGridData(graphs.yRange);
        else if (set === 'Adecuado')
            setGraphGridData(graphs.gRange);
        else if (set === 'Exceso de agua')
            setGraphGridData(graphs.bRange);
        else
            setGraphGridData(graphs.all);
    }

    function getPercentage(total, value) {
        try {
            return (value / total) * 100
        } catch {
            return 0
        }
    }

    function executeRef(ref) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const stationPieBox = useMemo(() => <PieBox key='stations' cols={3} setGridData={setStationGrid}
        isGraphPieBox={false}
        data={
            [{
                name: intl.formatMessage({ id: 'app.admin.stationPieBox.normal', defaultMessage: 'Normal' }),
                y: stations.isNormal.length,
                color: 'green',
                custom: { percentage: getPercentage(stations.all.length, stations.isNormal.length), total: stations.all.length }
            },
            {
                name: intl.formatMessage({ id: 'app.admin.stationPieBox.lagging', defaultMessage: 'Lagging' }),
                y: stations.isLagging.length,
                color: 'orange',
                custom: { percentage: getPercentage(stations.all.length, stations.isLagging.length), total: stations.all.length }
            },
            {
                name: intl.formatMessage({ id: 'app.admin.stationPieBox.lowBattery', defaultMessage: 'Low Battery' }),
                y: stations.hasLowBattery.length,
                color: 'red',
                custom: { percentage: getPercentage(stations.all.length, stations.hasLowBattery.length), total: stations.all.length }
            }]
        }
        name={intl.formatMessage({ id: 'app.admin.stationPieBox.name', defaultMessage: 'Stations' })}
        all={stations.all.length}
    />, [stations, intl]);

    const graphPieBox = useMemo(() => {
        const severeStress = markers.filter(item => item.type === 'RED').length;
        const startStress = markers.filter(item => item.type === 'YELLOW').length;
        const optimal = markers.filter(item => item.type === 'GREEN').length;
        const excessOfWater = markers.filter(item => item.type === 'BLUE').length;
        const total = severeStress + startStress + optimal + excessOfWater;

        return <PieBox key='paw graphs' cols={3} setGridData={setGraphGrid}
            isGraphPieBox={true}
            setFilter={setFilter}
            insideMap={insideMap}
            data={[
                {
                    id: "Stress severo",
                    name: intl.formatMessage({ id: 'app.admin.map.severeStress', defaultMessage: 'Severe stress' }),
                    y: severeStress,
                    color: '#EF6666',
                    custom: { percentage: getPercentage(total, severeStress), total: total }
                },
                {
                    id: "Inicia stress",
                    name: intl.formatMessage({ id: 'app.admin.map.startStress', defaultMessage: 'Start stress' }),
                    y: startStress,
                    color: '#EFEF66',
                    custom: { percentage: getPercentage(total, startStress), total: total }

                },
                {
                    id: "Adecuado",
                    name: intl.formatMessage({ id: 'app.admin.map.optimal', defaultMessage: 'Optimal' }),
                    y: optimal,
                    color: '#5DAE5D',
                    custom: { percentage: getPercentage(total, optimal), total: total }
                },
                {
                    id: "Exceso de agua",
                    name: intl.formatMessage({ id: 'app.admin.map.excessOfWater', defaultMessage: 'Excess of water' }),
                    y: excessOfWater,
                    color: '#00BFFF',
                    custom: { percentage: getPercentage(total, excessOfWater), total: total }
                }
            ]}
            name={intl.formatMessage({ id: 'app.admin.graphPieBox.name', defaultMessage: 'Paw Graphs' })}
        />
    }, [markers, intl])

    const userPieBox = useMemo(() => <PieBox key='users' type='info' cols={3}
        isGraphPieBox={false}
        data={[
            {
                name: intl.formatMessage({ id: 'app.admin.userPieBox.today', defaultMessage: 'Today' }),
                y: users.today.length,
                color: 'green',
                custom: { percentage: getPercentage(users.all.length, users.today.length), total: users.all.length }
            },
            {
                name: intl.formatMessage({ id: 'app.admin.userPieBox.thisMonth', defaultMessage: 'This Month' }),
                y: users.thisMonth.length,
                color: 'blue',
                custom: { percentage: getPercentage(users.all.length, users.thisMonth.length - users.today.length), total: users.all.length }
            },
            {
                name: intl.formatMessage({ id: 'app.admin.userPieBox.inactive', defaultMessage: 'Inactive' }),
                y: users.inactive.length,
                color: 'red',
                custom: { percentage: getPercentage(users.all.length, users.inactive.length), total: users.all.length }
            }
        ]}
        name={intl.formatMessage({ id: 'app.admin.userPieBox.name', defaultMessage: 'Users' })}
        all={users.all.length}
    />, [users, intl])

    useEffect(() => {
        async function fetchMapConfig() {
            try {
                const config = await getSecureConfig('map');
                setMapConfig(config);
            } catch (error) {
                console.error('Failed to load map configuration:', error);
            }
        }

        if (showNewMap) {
            fetchMapConfig();
        }
    }, [showNewMap]);

    // Merged toast/chip anchored to a map corner. Mounts expanded with a one-line announcement whose
    // action is an inline link, then collapses (auto-timer or close button) into the switch/return chip.
    const renderMapToast = (position) => {
        const toastLink = (chunks) => (
            <button
                key="map-toast-link"
                onClick={() => setShowNewMap(prev => !prev)}
                tabIndex={mapToastExpanded ? 0 : -1}
                className="underline font-medium text-emerald-300 hover:text-emerald-200 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
            >
                {chunks}
            </button>
        );
        return (
        <div className={`absolute ${position} z-[1150] flex items-center rounded-full bg-slate-900/95 text-white shadow-xl py-2 max-w-[calc(100%-1.5rem)] transition-all duration-500 motion-reduce:transition-none ${showNewMap && mapToastExpanded ? 'pl-3 pr-2' : 'px-3'}`}>
            {showNewMap && (
                <i className={`material-icons text-emerald-300 text-[18px] leading-none shrink-0 overflow-hidden transition-all duration-500 motion-reduce:transition-none ${mapToastExpanded ? 'max-w-[18px] opacity-100' : 'max-w-0 opacity-0'}`}>check_circle</i>
            )}
            <span className={`text-xs sm:text-sm truncate transition-all duration-500 motion-reduce:transition-none ${mapToastExpanded ? `max-w-[520px] opacity-100 ${showNewMap ? 'ml-2' : ''}` : 'max-w-0 opacity-0 ml-0'}`}>
                {showNewMap
                    ? intl.formatMessage(
                        { id: 'app.admin.map.toast.newMap', defaultMessage: 'New map enabled — enriched with satellite-based ET and more. <link>Return to classic map</link>' },
                        { link: toastLink }
                    )
                    : intl.formatMessage(
                        { id: 'app.admin.map.toast.oldMap', defaultMessage: '<link>Try the new map</link> — enriched with satellite-based ET and more.' },
                        { link: toastLink }
                    )}
            </span>
            <button
                onClick={() => setShowNewMap(prev => !prev)}
                tabIndex={mapToastExpanded ? -1 : 0}
                className={`text-xs font-semibold uppercase tracking-wide whitespace-nowrap overflow-hidden text-emerald-300 hover:text-emerald-200 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 transition-all duration-500 motion-reduce:transition-none ${mapToastExpanded ? 'max-w-0 opacity-0 ml-0 pointer-events-none' : 'max-w-[240px] opacity-100'}`}
            >
                {showNewMap
                    ? intl.formatMessage({ id: 'app.admin.map.banner.returnLink', defaultMessage: 'Return to classic map' })
                    : intl.formatMessage({ id: 'app.admin.map.banner.switchButton', defaultMessage: 'Try the new map' })}
            </button>
            <button
                onClick={() => setMapToastExpanded(false)}
                aria-label="Dismiss"
                tabIndex={mapToastExpanded ? 0 : -1}
                className={`shrink-0 flex items-center justify-center rounded-full hover:bg-white/10 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 transition-all duration-500 motion-reduce:transition-none ${mapToastExpanded ? 'w-6 h-6 opacity-100 ml-2' : 'w-0 h-6 opacity-0 ml-0 pointer-events-none'}`}
            >
                <i className="material-icons text-[16px] leading-none text-slate-300">close</i>
            </button>
        </div>
        );
    };

    return (
        <div ref={adminPageTopRef} className='mdl-grid p-0 lg:p-2'>
            <RefreshPortal>
                <RefreshButton onClick={refresh} loading={loading} />
            </RefreshPortal>
            <div className='breadcrumb reports_page-breadcrumb mdl-grid mdl-cell mdl-cell--12-col'>
                {intl.formatMessage({ id: 'app.admin.reports', defaultMessage: 'REPORTS' })}
            </div>
            <div className='mdl-cell mdl-cell--12-col'>
                <GotoTopFAB adminPageTopRef={adminPageTopRef} executeRef={executeRef} />
                {/* <PiesContainer>
                    {stationPieBox}  
                    {graphPieBox}
                    {userPieBox}
                </PiesContainer>
                <MapContainer>
                    {maps.map( map => <GMap key={map.id} map={map} stations={stations.all}/>)}
                </MapContainer> */}


                {/* </div>
            <div> */}
                <div ref={tabsRef} className="mdl-tabs mdl-js-tabs mdl-js-ripple-effect">
                    <div className="mdl-tabs__tab-bar">
                        <a href="#paw-panel" className="mdl-tabs__tab is-active">{intl.formatMessage({ id: 'app.admin.reports.plant', defaultMessage: 'REPORTS' })}</a>
                        <a href="#stations-panel" className="mdl-tabs__tab">
                            {(isAdmin || isOrganization) ?
                                intl.formatMessage({ id: 'app.admin.reports.station_health_users', defaultMessage: "STATION HEALTH / USERS" }) :
                                intl.formatMessage({ id: 'app.admin.reports.station_health', defaultMessage: 'STATION HEALTH' })}
                        </a>
                        <a href="#weather-report" className="mdl-tabs__tab">
                            {intl.formatMessage({ id: 'app.admin.reports.weather', defaultMessage: 'WEATHER' })}
                        </a>
                        {/* <a href="#copy-panel" className="mdl-tabs__tab">
                            {(isAdmin || isOrganization) && 'Copy Charts / Dashboards'}
                        </a> */}
                        <a href="#forecast" className="mdl-tabs__tab">
                            {intl.formatMessage({ id: 'app.admin.reports.forecast', defaultMessage: 'FORECAST' })}
                        </a>
                        <a href="#wells-report" className="mdl-tabs__tab">
                            {intl.formatMessage({ id: 'app.admin.reports.wells', defaultMessage: 'WELLS' })}
                        </a>
                    </div>
                    <div className="mdl-tabs__panel is-active" id="paw-panel">
                        <PiesContainer>
                            {!showNewMap && (insideMap == false && graphPieBox)}

                            {!showNewMap &&
                                <div className="relative lg:static h-[calc(100vh-200px)] min-h-[440px] flex flex-row w-full overflow-hidden">
                                    <div
                                        style={{ transition: 'width 0.3s' }}
                                        className={`relative h-full ${showSidePanel ? 'lg:w-3/5 lg:mr-4' : 'w-full'}`}
                                    >
                                        {/* Merged toast/chip — top-right; classic's own controls sit top-left, pie overlay bottom-left */}
                                        {renderMapToast('top-3 right-3')}
                                        {
                                            maps.map(map => <GMap
                                                handlePawGraphFilter={handlePawGraphFilter}
                                                handleShowAnalysis={handleShowAnalysis}
                                                filter={filter}
                                                graphPieBox={graphPieBox}
                                                insideMap={insideMap}
                                                key={map.id}
                                                map={map}
                                                isAdmin={isAdmin}
                                                stations={stations.all}
                                                markers={markers}
                                                showSidePanel={showSidePanel}
                                            />)
                                        }
                                    </div>

                                    {showSidePanel && (<div className='relative flex flex-col w-full h-full z-50 bg-white lg:static lg:!w-2/5 lg:border-l lg:border-gray-300 border border-gray-100 shadow-md'>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid #eee', background: '#fff' }}>
                                            <h4 style={{ margin: 0, fontSize: '1.2em' }}>{sidePanelTitle || 'Analysis'}</h4>
                                            <button className="mdl-button mdl-js-button mdl-button--icon" onClick={() => setShowSidePanel(false)}>
                                                <i className="material-icons">close</i>
                                            </button>
                                        </div>
                                        {showCurtain && <div
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                zIndex: 1000,
                                                pointerEvents: 'auto',
                                            }}
                                            onWheel={(e) => e.preventDefault()}
                                            onTouchMove={(e) => e.preventDefault()}
                                        >
                                            {/* Centered spinner, white color, no additional backdrop */}
                                            <Spinner
                                                color={'white'}
                                                show={true}
                                                backDrop={false}
                                                size={30}
                                            />
                                        </div>}
                                        <div style={{ overflowY: 'auto', flex: 1 }}>
                                            <Dashboard
                                                key={selectedDashboardId}
                                                tabId={selectedDashboardId}
                                                isActive={true}
                                                intl={intl}
                                                scrollToChartId={selectedChartId}
                                                hideHeader={true}
                                                isSidePanel={true}
                                                editWidgets={true}
                                                isSharedDash={false}
                                                // Pass other necessary props if Dashboard needs them to function correctly without wrapper
                                                tabs={[]} // Mock if needed or fetch
                                                loadStations={() => { }} // Mock
                                                setShowCurtain={setShowCurtain}
                                            />
                                        </div>
                                    </div>
                                    )}
                                </div>
                            }
                            {showNewMap && (
                                <div className='relative w-full h-[calc(100vh-200px)] min-h-[440px] overflow-hidden'>
                                    {/* Merged toast/chip — bottom-left, lifted above AgvMap's "mode:" label in that corner */}
                                    {renderMapToast('bottom-8 left-3')}
                                    {mapAuthToken ? (
                                    <AgvMap
                                        style={{ width: '100%' }}
                                        authToken={`Bearer ${mapAuthToken}`}
                                        locale={intl.locale}
                                        messages={intl.messages}
                                        configs={{
                                            VITE_MAPTILER_ACCESS_KEY: process.env.REACT_APP_MAPTILER_ACCESS_KEY,
                                            VITE_SENTINAL_HUB_CLIENT_ID: process.env.REACT_APP_SENTINAL_HUB_CLIENT_ID,
                                            VITE_SENTINAL_HUB_CLIENT_SECRET: process.env.REACT_APP_SENTINAL_HUB_CLIENT_SECRET,
                                            VITE_SENTINAL_HUB_WMTS_ID: process.env.REACT_APP_SENTINAL_HUB_WMTS_ID,
                                            username: props.username
                                        }}
                                    />
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <Spinner />
                                        </div>
                                    )}
                                </div>
                            )}
                        </PiesContainer>
                        <StationsPawStatusChart />
                        <GridContainer>
                            <GraphGrid
                                graphsRef={graphsRef}
                                data={graphGridData}
                                setData={setGraphGrid}
                            />
                        </GridContainer>

                    </div>
                    <div className="mdl-tabs__panel" id="stations-panel">
                        <PiesContainer>
                            <div className={`flex  ${(isAdmin || isOrganization) ? 'justify-around' : 'justify-start'} w-full flex-wrap`}>
                                {stationPieBox}
                                {(isAdmin || isOrganization) && userPieBox}
                            </div>
                        </PiesContainer>
                        <GridContainer>
                        </GridContainer>
                        {(isAdmin || isOrganization) &&
                            <UserGrid
                                isAdmin={isAdmin}
                                isOrganization={isOrganization}
                                data={users}
                                usersRef={usersRef}
                            />
                        }
                        <StationGrid
                            stationsRef={stationsRef}
                            data={stationGridData}
                            setData={setStationGrid}
                            edit={enterStationEdit}
                            cancel={cancelStationEdit}
                            update={updateStation}
                            onChange={onStationChange}
                            remove={enterDelete}
                            percentOfOrgsSensorsWithoutData={percentOfOrgsSensorsWithoutData}
                        />
                        {toDelete !== undefined &&
                            <ConfirmDeleteDialog
                                onClose={() => setToDelete(undefined)}
                                remove={deleteStation}
                                deleting={deleting}
                                message={<p> <b>{toDelete.name} [{toDelete.serial}] </b>{intl.formatMessage({ id: 'app.chart.deleteChart.deleteMessage', defaultMessage: "will be deleted along with all of it's data. Type {delete} below to confirm." }, { delete: <span style={{ color: 'red' }}>DELETE</span> })}</p>}
                            />
                        }
                    </div>
                    <div className="mdl-tabs__panel" id="weather-report">
                        <WeatherReport
                            setRefreshAll={setRefreshAll}
                            refreshAll={refreshAll}
                            stations={stations.all}
                            logout={props.logout}
                        />
                    </div>
                    {/* <div className="mdl-tabs__panel" id="copy-panel">
                        {(isAdmin || isOrganization) && 
                        <React.Fragment>
                            <CopyChart  users= {users.all} />
                            <CopyDashboard users={users.all} />
                        </React.Fragment>
                        }
                    </div> */}
                    <div className="mdl-tabs__panel" id="forecast">
                        <ForecastCharts
                            charts={forecastCharts}
                        />
                    </div>
                    <div className="mdl-tabs__panel" id="wells-report">
                        <WellsReportChartContainer
                            charts={wellsCharts}
                        />
                    </div>

                </div>
            </div>
        </div>
    );
}


export default Admin;


function GotoTopFAB(props) {
    return (
        <div onClick={() => props.executeRef(props.adminPageTopRef)} className='ag-add-widget-button'>
            <button className="mdl-button mdl-js-button mdl-button--fab mdl-js-ripple-effect mdl-button--colored mdl-shadow--4dp mdl-color--accent">
                <i className="material-icons">arrow_upward</i>
            </button>
        </div>
    )
}
